/**
 * Bidirectional Links Service
 * 
 * Handles syncing entity connections in both directions.
 * When entity A links to entity B, entity B should also link back to A.
 */

import { getDocument, updateDocument, COLLECTIONS } from './firestore'

// Mapping: what field does each collection use to store links TO another collection?
// e.g., characters store links to events in 'linkedEvents'
const SOURCE_LINK_FIELDS = {
  [COLLECTIONS.CHARACTERS]: {
    [COLLECTIONS.LOCATIONS]: 'linkedLocations',
    [COLLECTIONS.EVENTS]: 'linkedEvents',
    [COLLECTIONS.SHOTS]: 'linkedShots',
  },
  [COLLECTIONS.LOCATIONS]: {
    [COLLECTIONS.CHARACTERS]: 'linkedCharacters',
    [COLLECTIONS.EVENTS]: 'linkedEvents',
    [COLLECTIONS.SHOTS]: 'linkedShots',
  },
  [COLLECTIONS.EVENTS]: {
    [COLLECTIONS.CHARACTERS]: 'linkedCharacters',
    [COLLECTIONS.LOCATIONS]: 'linkedLocations',
    [COLLECTIONS.SHOTS]: 'linkedShots',
  },
  [COLLECTIONS.SHOTS]: {
    [COLLECTIONS.CHARACTERS]: 'linkedCharacters',
    [COLLECTIONS.LOCATIONS]: 'linkedLocations',
    [COLLECTIONS.EVENTS]: 'linkedEvents',
  },
}

// Mapping from link field name back to collection
const LINK_FIELD_TO_COLLECTION = {
  linkedCharacters: COLLECTIONS.CHARACTERS,
  linkedLocations: COLLECTIONS.LOCATIONS,
  linkedEvents: COLLECTIONS.EVENTS,
  linkedShots: COLLECTIONS.SHOTS,
}

// All link field names
const ALL_LINK_FIELDS = ['linkedCharacters', 'linkedLocations', 'linkedEvents', 'linkedShots']

/**
 * Update bidirectional links after an entity is saved
 * 
 * @param {string} projectId - The project ID
 * @param {string} sourceCollection - Collection of entity being saved (e.g., 'characters')
 * @param {string} sourceEntityId - ID of entity being saved
 * @param {object|null} oldData - Previous entity data (null for create)
 * @param {object} newData - New entity data
 */
export async function updateBidirectionalLinks(projectId, sourceCollection, sourceEntityId, oldData, newData) {
  const updates = []

  for (const linkField of ALL_LINK_FIELDS) {
    const targetCollection = LINK_FIELD_TO_COLLECTION[linkField]
    
    // Skip if linking to same collection (shouldn't happen, but safety check)
    if (targetCollection === sourceCollection) continue

    const oldIds = oldData?.[linkField] || []
    const newIds = newData[linkField] || []

    // Find which IDs were added and which were removed
    const addedIds = newIds.filter(id => !oldIds.includes(id))
    const removedIds = oldIds.filter(id => !newIds.includes(id))

    // Determine what field on the target entity stores links back to our source
    const reverseField = SOURCE_LINK_FIELDS[targetCollection]?.[sourceCollection]
    if (!reverseField) continue

    // Add source entity to newly linked entities
    for (const targetId of addedIds) {
      updates.push(
        addLinkToEntity(projectId, targetCollection, targetId, reverseField, sourceEntityId)
      )
    }

    // Remove source entity from unlinked entities  
    for (const targetId of removedIds) {
      updates.push(
        removeLinkFromEntity(projectId, targetCollection, targetId, reverseField, sourceEntityId)
      )
    }
  }

  // Execute all updates in parallel
  if (updates.length > 0) {
    await Promise.all(updates)
  }
}

/**
 * Add a link to an entity's link field (if not already present)
 */
async function addLinkToEntity(projectId, collection, entityId, linkField, idToAdd) {
  try {
    const entity = await getDocument(projectId, collection, entityId)
    if (!entity) {
      console.warn(`[bidirectionalLinks] Entity not found: ${collection}/${entityId}`)
      return
    }

    const currentLinks = entity[linkField] || []
    if (!currentLinks.includes(idToAdd)) {
      await updateDocument(projectId, collection, entityId, {
        [linkField]: [...currentLinks, idToAdd]
      })
    }
  } catch (err) {
    console.warn(`[bidirectionalLinks] Failed to add link to ${collection}/${entityId}:`, err)
  }
}

/**
 * Remove a link from an entity's link field (if present)
 */
async function removeLinkFromEntity(projectId, collection, entityId, linkField, idToRemove) {
  try {
    const entity = await getDocument(projectId, collection, entityId)
    if (!entity) {
      // Entity might have been deleted - that's okay
      return
    }

    const currentLinks = entity[linkField] || []
    if (currentLinks.includes(idToRemove)) {
      await updateDocument(projectId, collection, entityId, {
        [linkField]: currentLinks.filter(id => id !== idToRemove)
      })
    }
  } catch (err) {
    console.warn(`[bidirectionalLinks] Failed to remove link from ${collection}/${entityId}:`, err)
  }
}

/**
 * Clean up all bidirectional links when an entity is deleted
 * 
 * @param {string} projectId - The project ID
 * @param {string} sourceCollection - Collection of entity being deleted
 * @param {string} sourceEntityId - ID of entity being deleted
 * @param {object} entityData - The entity data (to find what it was linked to)
 */
export async function removeBidirectionalLinksOnDelete(projectId, sourceCollection, sourceEntityId, entityData) {
  const updates = []

  for (const linkField of ALL_LINK_FIELDS) {
    const targetCollection = LINK_FIELD_TO_COLLECTION[linkField]
    
    if (targetCollection === sourceCollection) continue

    const linkedIds = entityData[linkField] || []
    
    // Determine what field on the target entity stores links back to our source
    const reverseField = SOURCE_LINK_FIELDS[targetCollection]?.[sourceCollection]
    if (!reverseField) continue

    // Remove source entity from all linked entities
    for (const targetId of linkedIds) {
      updates.push(
        removeLinkFromEntity(projectId, targetCollection, targetId, reverseField, sourceEntityId)
      )
    }
  }

  if (updates.length > 0) {
    await Promise.all(updates)
  }
}

export default {
  updateBidirectionalLinks,
  removeBidirectionalLinksOnDelete,
}
